import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal } from '@/components/ui'

interface ClubFormModalProps {
  title: string
  submitLabel: string
  initial?: { name: string; description: string | null }
  isPending: boolean
  error?: boolean
  onSubmit: (values: { name: string; description: string }) => void
  onClose: () => void
}

/** Create/edit club form */
export function ClubFormModal({
  title,
  submitLabel,
  initial,
  isPending,
  error,
  onSubmit,
  onClose,
}: ClubFormModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    onSubmit({ name: name.trim(), description: description.trim() })
  }

  return (
    <Modal isOpen onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4 p-6">
        <div className="space-y-2">
          <label htmlFor="club-name" className="text-sm font-medium text-foreground">
            {t('clubs.form.name')}
          </label>
          <Input
            id="club-name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('clubs.form.namePlaceholder')}
            autoFocus
            required
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="club-description" className="text-sm font-medium text-foreground">
            {t('clubs.form.description')}{' '}
            <span className="font-normal text-muted-foreground">({t('common.optional')})</span>
          </label>
          <textarea
            id="club-description"
            value={description}
            maxLength={500}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('clubs.form.descriptionPlaceholder')}
            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {error && <p className="text-sm text-error">{t('errors.general')}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isPending || !name.trim()}>
            {isPending ? t('common.saving') : submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
